import { useCallback, useEffect, useState } from 'react';
import { AppState, AppStateStatus } from 'react-native';

import socketService from '../services/socket.service';

interface UseSocketOptions {
  token?: string | null;
  autoConnect?: boolean;
  reconnectInterval?: number;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: any) => void;
}

interface SocketContextValue {
  connected: boolean;
  isConnected: boolean;
  isConnecting: boolean;
  reconnectAttempts: number;
  connect: () => Promise<void>;
  disconnect: () => void;
  emit: (event: string, ...args: any[]) => void;
  on: (event: string, callback: (...args: any[]) => void) => void;
  off: (event: string, callback?: (...args: any[]) => void) => void;
}

/**
 * Socket Provider Hook - 用于 App.tsx 中创建 Socket Context
 */
export function useSocketProvider(token: string | null): SocketContextValue {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);

  const connect = useCallback(async () => {
    if (!token) return;

    setIsConnecting(true);
    try {
      await socketService.connect(token);
      setIsConnected(true);
      setReconnectAttempts(0);
    } catch (error) {
      setIsConnected(false);
      console.error('Socket connect error:', error);
    } finally {
      setIsConnecting(false);
    }
  }, [token]);

  const disconnect = useCallback(() => {
    socketService.disconnect();
    setIsConnected(false);
    setIsConnecting(false);
  }, []);

  // 自动连接
  useEffect(() => {
    if (token) {
      connect();
    }

    return () => {
      socketService.disconnect();
    };
  }, [token, connect]);

  // 监听 socket 事件
  useEffect(() => {
    const socket = socketService.getSocket();

    if (socket) {
      socket.on('connect', () => {
        setIsConnected(true);
      });

      socket.on('disconnect', () => {
        setIsConnected(false);

        // 自动重连
        setTimeout(() => {
          if (token && !socketService.isConnected()) {
            setReconnectAttempts((prev) => prev + 1);
            connect();
          }
        }, 5000);
      });

      socket.on('error', () => {
        setIsConnected(false);
      });
    }
  }, [token, connect]);

  // 监听应用状态
  useEffect(() => {
    const handleAppStateChange = (nextAppState: AppStateStatus) => {
      if (nextAppState === 'active' && token && !socketService.isConnected()) {
        connect();
      }
    };

    const subscription = AppState.addEventListener('change', handleAppStateChange);

    return () => {
      subscription.remove();
    };
  }, [token, connect]);

  const emit = useCallback((event: string, ...args: any[]) => {
    socketService.emit(event, ...args);
  }, []);

  const on = useCallback((event: string, callback: (...args: any[]) => void) => {
    socketService.on(event, callback);
  }, []);

  const off = useCallback((event: string, callback?: (...args: any[]) => void) => {
    socketService.off(event, callback);
  }, []);

  return {
    connected: isConnected,
    isConnected,
    isConnecting,
    reconnectAttempts,
    connect,
    disconnect,
    emit,
    on,
    off,
  };
}

/**
 * useSocket Hook - 用于组件中访问 Socket（需要在 SocketContext.Provider 内使用）
 */
export function useSocket(options: UseSocketOptions = {}) {
  const {
    token,
    autoConnect = true,
    reconnectInterval = 5000,
    onConnect,
    onDisconnect,
    onError,
  } = options;

  const [isConnected, setIsConnected] = useState(socketService.isConnected());
  const [isConnecting, setIsConnecting] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);

  const connect = useCallback(async () => {
    if (!token) return;

    setIsConnecting(true);
    try {
      socketService.connect(token);
      setIsConnected(true);
      setReconnectAttempts(0);
      onConnect?.();
    } catch (error) {
      setIsConnected(false);
      onError?.(error);
    } finally {
      setIsConnecting(false);
    }
  }, [token, onConnect, onError]);

  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    const socket = socketService.getSocket();

    if (socket) {
      socket.on('connect', () => {
        setIsConnected(true);
        onConnect?.();
      });

      socket.on('disconnect', () => {
        setIsConnected(false);
        onDisconnect?.();

        setTimeout(() => {
          if (!socketService.isConnected()) {
            setReconnectAttempts((prev) => prev + 1);
            connect();
          }
        }, reconnectInterval);
      });

      socket.on('error', (error: any) => {
        setIsConnected(false);
        onError?.(error);
      });
    }
  }, [autoConnect, reconnectInterval, onConnect, onDisconnect, onError, connect]);

  const disconnect = useCallback(() => {
    socketService.disconnect();
    setIsConnected(false);
    setIsConnecting(false);
  }, []);

  const emit = useCallback((event: string, ...args: any[]) => {
    socketService.emit(event, ...args);
  }, []);

  const on = useCallback((event: string, callback: (...args: any[]) => void) => {
    socketService.on(event, callback);
  }, []);

  const off = useCallback((event: string, callback?: (...args: any[]) => void) => {
    socketService.off(event, callback);
  }, []);

  return {
    connected: isConnected,
    isConnected,
    isConnecting,
    reconnectAttempts,
    connect,
    disconnect,
    emit,
    on,
    off,
  };
}

// 使用示例：监听特定事件
export function useSocketEvent<T = any>(event: string, handler: (data: T) => void) {
  const { on, off } = useSocket();

  useEffect(() => {
    on(event, handler);

    return () => {
      off(event, handler);
    };
  }, [event, handler, on, off]);
}
